import { client } from '@api/rest/client';
import type {
  IntelligenceQuestionDTO,
  IntelligenceQuestionsResponseDTO,
} from '@conversed/rest/src/contracts/home/home.dto';
import type { Sentiment } from '@conversed/shared/src/central/constants/sentiment';
import type { TimeWindow } from '@conversed/shared/src/central/types/time-window.type';
import { useCallback, useEffect, useRef, useState } from 'react';
import { toastErrorTranslated } from '../../helpers/toast';

type UseTopicQuestionsArgs = {
  topicId: string;
  timeWindow: TimeWindow;
  // Null shows every question; a sentiment shows only that bucket.
  sentiment: Sentiment | null;
  // How many questions the selected bucket holds (from the topic's buckets). A
  // zero is known to be empty, so no request is sent for it.
  selectedBucketCount: number;
  // The unfiltered first page, fetched before the modal opened.
  initialPage: IntelligenceQuestionsResponseDTO;
};

// Fetches one page; the cursor travels as two flat query strings.
export const fetchTopicQuestionsPage = async ({
  topicId,
  timeWindow,
  sentiment,
  cursor,
}: {
  topicId: string;
  timeWindow: TimeWindow;
  sentiment?: Sentiment | null;
  cursor?: IntelligenceQuestionsResponseDTO['nextCursor'];
}): Promise<IntelligenceQuestionsResponseDTO> => {
  const { data } = await client.home.getIntelligenceQuestions({
    query: {
      topicId,
      window: timeWindow,
      sentiment: sentiment ?? undefined,
      cursorCreatedAt: cursor?.createdAt,
      cursorIds: cursor?.ids.join(','),
    },
  });
  return data;
};

/**
 * Every question of a topic in the selected window, loaded page by page as the
 * View modal's list scrolls. Changing the sentiment filter starts over from the
 * first page of that bucket; clearing it restores the unfiltered first page
 * without a request. A response that arrives after the filter moved on is dropped.
 *
 * While a new filter loads, the previous list stays in place (`isReplacing`) so
 * the modal can dim it instead of emptying and refilling; `listVersion` changes
 * whenever the list is swapped for a different one, so the modal can replay its
 * fade-in and scroll back to the top.
 *
 * A failed page sets `hasError` and stops further loading until `retry` is
 * called - otherwise the still-visible scroll sentinel would re-request the same
 * page in a loop, with a toast each time.
 */
export const useTopicQuestions = ({
  topicId,
  timeWindow,
  sentiment,
  selectedBucketCount,
  initialPage,
}: UseTopicQuestionsArgs) => {
  const [questions, setQuestions] = useState<IntelligenceQuestionDTO[]>(
    initialPage.questions,
  );
  const [nextCursor, setNextCursor] = useState(initialPage.nextCursor);
  const [isLoading, setIsLoading] = useState(false);
  const [hasError, setHasError] = useState(false);
  const [isReplacing, setIsReplacing] = useState(false);
  const [listVersion, setListVersion] = useState(0);
  const requestIdRef = useRef(0);
  const isFirstRenderRef = useRef(true);

  const loadPage = useCallback(
    async (
      cursor: IntelligenceQuestionsResponseDTO['nextCursor'],
      shouldAppend: boolean,
    ) => {
      requestIdRef.current += 1;
      const requestId = requestIdRef.current;
      setIsLoading(true);
      setIsReplacing(!shouldAppend);
      setHasError(false);
      try {
        const page = await fetchTopicQuestionsPage({
          topicId,
          timeWindow,
          sentiment,
          cursor,
        });
        if (requestId !== requestIdRef.current) {
          return;
        }
        setQuestions((current) =>
          shouldAppend ? [...current, ...page.questions] : page.questions,
        );
        setNextCursor(page.nextCursor);
        if (!shouldAppend) {
          setListVersion((version) => version + 1);
        }
      } catch (error) {
        if (requestId !== requestIdRef.current) {
          return;
        }
        console.error(error);
        toastErrorTranslated(error);
        setHasError(true);
        // * A failed filter must not leave the previous filter's rows on screen
        // * under the new chip.
        if (!shouldAppend) {
          setQuestions([]);
          setNextCursor(null);
        }
      } finally {
        if (requestId === requestIdRef.current) {
          setIsLoading(false);
          setIsReplacing(false);
        }
      }
    },
    [topicId, timeWindow, sentiment],
  );

  useEffect(() => {
    // The first render already holds the unfiltered first page.
    if (isFirstRenderRef.current) {
      isFirstRenderRef.current = false;
      return;
    }

    if (sentiment !== null && selectedBucketCount > 0) {
      // Keeps the current rows on screen, dimmed, until the bucket arrives.
      void loadPage(null, false);
      return;
    }

    requestIdRef.current += 1;
    setHasError(false);
    setIsLoading(false);
    setIsReplacing(false);
    setListVersion((version) => version + 1);

    if (sentiment === null) {
      setQuestions(initialPage.questions);
      setNextCursor(initialPage.nextCursor);
      return;
    }

    setQuestions([]);
    setNextCursor(null);
  }, [sentiment, selectedBucketCount, initialPage, loadPage]);

  const loadMore = useCallback(() => {
    if (isLoading || hasError || !nextCursor) {
      return;
    }
    void loadPage(nextCursor, true);
  }, [isLoading, hasError, nextCursor, loadPage]);

  // Re-requests the page that failed: the next page when some are loaded, else
  // the first page of the current filter.
  const retry = useCallback(() => {
    const isFirstPage = questions.length === 0;
    void loadPage(isFirstPage ? null : nextCursor, !isFirstPage);
  }, [questions.length, nextCursor, loadPage]);

  return {
    questions,
    hasMore: nextCursor !== null,
    isLoading,
    hasError,
    isReplacing,
    listVersion,
    loadMore,
    retry,
  };
};
